"use client";

import { c as _c } from "react/compiler-runtime";
import type { CollectionNavItem } from "@/types/segments";
import { useEffect, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { sendGTMEvent } from "@next/third-parties/google";
import { useCarFilters } from "@/hooks/useCarFilters";
import { useClientSearch } from "@/actions/clientSearchActions";
import { SearchInput } from "@/components/cars/SearchInputOptimized";
import QuickFilters from "./QuickFilters";
import SortingControls from "@/components/cars/SortingControls";
import { useMobileFilter } from "./MobileFilterProvider";
interface TopSearchBarProps {
  className?: string;
  collections?: CollectionNavItem[];
}
export default function TopSearchBar(t0) {
  const $ = _c(35);
  const {
    className: t1,
    collections: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const collections = t3;
  const {
    showMobileFilters,
    setShowMobileFilters
  } = useMobileFilter();
  const {
    searchQuery,
    hasActiveFilters
  } = useCarFilters();
  const {
    performSearch,
    isSearching
  } = useClientSearch();
  const [localSearchInput, setLocalSearchInput] = useState(searchQuery);
  const skipNextSyncRef = useRef(false);
  let t4;
  let t5;
  if ($[2] !== searchQuery) {
    t4 = () => {
      if (skipNextSyncRef.current) {
        skipNextSyncRef.current = false;
        return;
      }
      setLocalSearchInput(searchQuery);
    };
    t5 = [searchQuery];
    $[2] = searchQuery;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] !== localSearchInput || $[6] !== performSearch) {
    t6 = () => {
      sendGTMEvent({
        event: "search",
        search_term: localSearchInput,
        search_location: "top_search_bar",
        has_search_term: Boolean(localSearchInput.trim())
      });
      performSearch(localSearchInput, true);
      setLocalSearchInput("");
      skipNextSyncRef.current = true;
    };
    $[5] = localSearchInput;
    $[6] = performSearch;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSearch = t6;
  let t7;
  if ($[8] !== setShowMobileFilters) {
    t7 = t8 => {
      const label = t8 === undefined ? "all" : t8;
      sendGTMEvent({
        event: "filter_toggle",
        filter_type: label,
        location: "top_search_bar"
      });
      setShowMobileFilters(true);
    };
    $[8] = setShowMobileFilters;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const openFilters = t7;
  const t8 = `t-border-b t-border-slate-200 t-bg-white ${className}`;
  let t9;
  if ($[10] !== handleSearch || $[11] !== isSearching || $[12] !== localSearchInput) {
    t9 = <div><div className="t-flex t-items-center t-gap-2"><div className="t-min-w-0 t-flex-1"><SearchInput value={localSearchInput} onChange={setLocalSearchInput} onSearch={handleSearch} placeholder="Search make, model, or keyword" isSearching={isSearching} aria-label="Search for cars" /></div></div></div>;
    $[10] = handleSearch;
    $[11] = isSearching;
    $[12] = localSearchInput;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== collections) {
    t10 = <QuickFilters collections={collections} className="t-order-3 t-w-full t-min-w-0 t-overflow-x-auto t-pb-1 lg:t-order-none lg:t-w-auto lg:t-flex-1" />;
    $[14] = collections;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== openFilters) {
    t11 = () => openFilters();
    $[16] = openFilters;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SlidersHorizontal className="t-h-4 t-w-4" aria-hidden="true" />;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== hasActiveFilters) {
    t13 = hasActiveFilters && <span className="t-h-2 t-w-2 t-rounded-full t-bg-brand-highlighter" aria-label="Filters applied" />;
    $[19] = hasActiveFilters;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== showMobileFilters || $[22] !== t11 || $[23] !== t13) {
    t14 = <button type="button" onClick={t11} aria-haspopup="dialog" aria-expanded={showMobileFilters} className="t-inline-flex t-min-h-11 t-items-center t-justify-center t-gap-2 t-rounded-none t-border t-border-brand-violet t-bg-brand-violet t-px-3 t-text-sm t-font-semibold t-text-white t-whitespace-nowrap t-transition-colors hover:t-bg-brand-violet-dark focus-visible:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2 lg:t-hidden">{t12}Shop Your Way{t13}</button>;
    $[21] = showMobileFilters;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="t-ml-auto t-flex-shrink-0 t-self-end sm:t-self-auto"><SortingControls /></div>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t10 || $[27] !== t14) {
    t16 = <div className="t-mt-2 t-flex t-flex-wrap sm:t-flex-nowrap t-items-center t-justify-between t-gap-2 lg:t-mt-3">{t10}{t14}{t15}</div>;
    $[26] = t10;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16 || $[30] !== t9) {
    t17 = <div className="t-px-4 t-py-3 lg:t-py-4 sm:t-px-6 lg:t-px-8">{t9}{t16}</div>;
    $[29] = t16;
    $[30] = t9;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17 || $[33] !== t8) {
    t18 = <div className={t8}>{t17}</div>;
    $[32] = t17;
    $[33] = t8;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
}